import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  Button,
  Chips,
  Choice,
  DataTable,
  Dialog,
  Drawer,
  EmptyState,
  Field,
  Icon,
  Input,
  Panel,
  Select,
  Stat,
  Tabs,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
  type TagTone,
} from '@dayon/design-system';
import {
  GAME_REASON_SEPARATOR,
  type ChangeGameStatusResult,
  type ChangeProviderStatusResult,
  type GameListResult,
  type GameListRow,
  type GameStatus,
  type GameStatusChangeReason,
  type ProviderStatus,
  type SetGameBrandsResult,
} from '@dayon/contracts';
import { formatCount, formatPesos, formatPesosCompact, formatRate } from '@dayon/money';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { ProviderReconciliation } from './ProviderReconciliation.tsx';
import { GameBrandsDialog, type PendingBrandChange } from './GameBrandsDialog.tsx';
import { BulkBrandsDialog } from './BulkBrandsDialog.tsx';
import { AddGameDialog } from './AddGameDialog.tsx';
import { AddProviderDialog } from './AddProviderDialog.tsx';
import { GameConfigDialog, type PendingGameConfig } from './GameConfigDialog.tsx';
import { ProviderConfigDialog, type PendingProviderConfig } from './ProviderConfigDialog.tsx';
import { fieldLabel, formatFieldValue, movesSentence } from './catalogue-write.ts';
import { ProviderStatusDialog, type PendingProviderChange } from './ProviderStatusDialog.tsx';
import { ProviderTransactions } from './ProviderTransactions.tsx';
import { shelfSentence, type BrandPair } from './game-brands.ts';
import { pictureListTag } from './game-picture.ts';
import { afterCountSentence } from './provider-status.ts';
import { hourlyImportName, jiliImportRanWords, notStoredReason, providerOwnsTitle } from './provider-catalogue.ts';
import { logReasonLabel, STATUS_REASONS } from './log-reasons.ts';

/**
 * Game management.
 *
 * One flat catalogue across every provider and brand, plus the two surfaces
 * that hang off it: the provider drill-down and the configuration log.
 *
 * READ THIS BEFORE ADDING AN ACTION. THREE writes exist here, each with its
 * own endpoint, its own reason code and its own row in the configuration log:
 *   · POST /api/games/status — moves any number of games to LIVE, MAINTENANCE
 *     or DISABLED in one transaction. The bulk bar and the drawer share it.
 *   · POST /api/games/providers/:id/status — one provider, from the Providers
 *     tab. It does NOT cascade to the games on that provider's shelf.
 *   · PUT /api/games/:id/brands — the whole chip set for one game, from the
 *     drawer. The route works out which pairs moved.
 * Add game, Add provider and Edit configuration ARE NOW WIRED, and the notices
 * moved in the same commit — the rule this comment used to state as a warning:
 *   · POST  /api/games/providers            — a provider, born DISABLED.
 *   · POST  /api/games                      — a title, born DISABLED and on no
 *     brand's shelf. Two further deliberate steps reach a player.
 *   · PATCH /api/games/:id/config           — a title's name, category, table
 *     type and declared RTP. Name and table type are READ-ONLY on an SA-driven
 *     title: the hourly host import owns both and would revert an edit within
 *     the hour, so the form refuses rather than letting it be discovered later.
 *   · PATCH /api/games/providers/:id/config — certified_at and revenue_share_bp,
 *     the two columns this screen reported as "missing, not blank" for its whole
 *     life. RECORDED, NOT ENFORCED: neither gates a launch, and the notices say
 *     so, because a date field appearing must not be read as a new gate.
 *
 * SEVEN WRITES, NOT THREE, AND ONE SHAPE BETWEEN THEM. Every one takes a reason
 * code from a closed set and a note of at least ten characters, applies in one
 * transaction with its audit row, and appends to the Configuration log tab —
 * which merges FIVE tables so an operator can read one chronological list.
 *
 * WHAT IS STILL NOT HERE, and each says so on screen: bet range, jackpot
 * contribution and demo mode. Those are not columns waiting to be added — the
 * launch path carries no such parameter, so a figure typed for one would reach
 * no player. See the drawer's own notice.
 */

/* ── Vocabulary ────────────────────────────────────────────────────────────
   The DB stores screaming snake case; the design writes sentence case. The
   mapping is one-way and lives here so no cell invents its own wording.
   "Table games", not "Table": the shared list's word (GAME_CATEGORY_LABELS in
   @dayon/contracts), which the Add and Edit dialogs and the games-hosted file
   say too (ruling R11). */
const CATEGORY_LABEL: Record<string, string> = {
  SLOTS: 'Slots',
  LIVE_CASINO: 'Live casino',
  CRASH: 'Crash',
  ARCADE: 'Arcade',
  FISHING: 'Fishing',
  BINGO: 'Bingo',
  TABLE: 'Table games',
};

const STATUS_LABEL: Record<string, string> = {
  LIVE: 'Live',
  MAINTENANCE: 'Maintenance',
  DISABLED: 'Disabled',
};

/**
 * A per-brand row moves on a DIFFERENT AXIS from a status row, and it gets its
 * own words for that reason.
 *
 * "Disabled" on a brand row would be read as the GAME being disabled, and it is
 * not: availability and status are independent columns, and the availability
 * write never touches status. `NOT SET` is the pair that had no row at all —
 * never considered for that brand, which is a different fact from having been
 * switched off, and the only one of the three that this table alone can say.
 *
 * Kept out of STATUS_LABEL deliberately: that map is read by the chips, the
 * game rows and the provider rows, and an availability word appearing in any of
 * them would be a status claim.
 */
const AVAILABILITY_LABEL: Record<string, string> = {
  ON: 'On',
  OFF: 'Off',
  'NOT SET': 'Not set',
};


/** On is a live shelf; off is a dark one; not set is neither, and says so. */
const availabilityTone = (value: string): TagTone =>
  value === 'ON' ? 'accent' : value === 'NOT SET' ? 'outline' : 'quiet';

/** The verb on the button, not the name of the state it lands in. */
const PROVIDER_ACTION_LABEL: Record<ProviderStatus, string> = {
  LIVE: 'Bring live',
  MAINTENANCE: 'Maintenance',
  DISABLED: 'Disable',
};

/** Live = filled accent, Maintenance = accent outline, Disabled = quiet. */
const statusTone = (status: string): TagTone =>
  status === 'LIVE' ? 'accent' : status === 'MAINTENANCE' ? 'outline' : 'quiet';

/**
 * The capability EVERY write on this screen is gated on — now exactly one key.
 *
 * It was two. `POST /api/games/status` spent its life borrowing `access.manage`
 * because the matrix had no `games.*` key and a VIEW permission must never gate
 * a WRITE, while the newer provider and shelf writes already required
 * `games.manage`. Two constants, two keys, one screen.
 *
 * The API moved the status route onto `games.manage`, so this moves with it.
 * THE SCREEN AND THE ROUTE MUST NAME THE SAME KEY OR THE SCREEN LIES IN ONE OF
 * TWO DIRECTIONS: gating on a key the route no longer checks REFUSES someone
 * the API would allow — and the refusal is worse than a broken button, because
 * it names a key and is therefore believed — while the reverse enables a
 * control that is going to come back 403.
 *
 * Super Admin holds both keys, so neither direction is visible on dev. What is
 * NOT visible there is the whole reason to change it: a principal holding
 * `access.manage` WITHOUT `games.manage` — which is precisely the separation
 * the API move exists to make possible — would be handed game-status controls
 * that the route refuses.
 *
 * ONE CONSTANT, not two that happen to hold the same string. Two names for one
 * key is how this drifted in the first place: the provider constant was updated
 * when `games.manage` arrived and the status one was not, and nothing could
 * say so, because a string was being compared with a string.
 */
const MANAGE_KEY = 'games.manage';

/* Why a game changed status (STATUS_REASONS), and every reason the Configuration log can hold
   by the label its dialog offered (logReasonLabel): log-reasons.ts. */

/** The reason most likely to be right for each direction — never the only one. */
const DEFAULT_REASON: Record<GameStatus, GameStatusChangeReason> = {
  LIVE: 'CATALOGUE_LAUNCH',
  MAINTENANCE: 'SCHEDULED_MAINTENANCE',
  DISABLED: 'COMMERCIAL_DECISION',
};

/** The dialog asks the question in the operator's words, not the column's. */
const CHANGE_TITLE: Record<GameStatus, (what: string) => string> = {
  LIVE: (what) => `Enable ${what}?`,
  MAINTENANCE: (what) => `Put ${what} into maintenance?`,
  DISABLED: (what) => `Disable ${what}?`,
};

/**
 * What the write ACTUALLY does, stated before the operator commits.
 *
 * One thing happens, in one transaction: `game.game.status` moves and a row
 * lands in `game.game_status_change` carrying the reason and the admin.
 *
 * THAT STATUS IS NOW READ OUTSIDE THIS CONSOLE, and these sentences were
 * rewritten when it started to be. `GET /api/player/games` serves the player
 * catalogue from these same rows: a DISABLED game leaves the shelf entirely, a
 * MAINTENANCE one stays visible and cannot be opened, and
 * `POST /api/player/games/:code/launch` refuses either with the status as the
 * reason. So a status change here IS a runtime effect on the player app, and
 * saying otherwise — which is what this dialog said until the catalogue was
 * wired — would now be the untrue half.
 *
 * What still does NOT happen: no round is closed, because `game.game_round` is
 * empty and nothing plays; no stake is returned, because nothing is staked; and
 * no player is notified. NOT because "nothing messages a player" — which is what
 * this comment said until 2026-10-09 and was false: an announcement send is a
 * mail fan-out. The true reason is narrower and is what the screen now says: a
 * status write touches the game row and never a round. Each sentence below
 * states what is recorded, what reaches the player app, and what does not
 * happen — a dialog promising a returned-stake message that no code sends is
 * worse than no dialog at all: the operator acts on it, and nobody finds out
 * until a player does. The provider check is still enforced at the moment of
 * the write as well as at a launch.
 */
const CHANGE_CONSEQUENCE: Record<GameStatus, string> = {
  /*
   * THIS SENTENCE WENT STALE ONCE ALREADY. It used to end "it does not make the
   * game openable — no provider is integrated", which was true of a catalogue of
   * MOCK fixtures and became FALSE the day SA Gaming went live and real money
   * moved through it. The same defect as the player shelf's lobby note: a
   * reassurance nobody drives goes on being shown after it stops being true.
   *
   * So it now says what depends on the PROVIDER rather than asserting a fact
   * about the whole catalogue, and it names the second step — the brand shelf —
   * because "I set it live and players still cannot see it" is the question this
   * dialog was being asked to answer and did not.
   */
  LIVE:
    'Records the game as live, with your reason, in the configuration log. This is ONE of the two ' +
    'things a player needs: a live game is still invisible to anyone until it is on a brand’s ' +
    'shelf, which you set by opening the game and using Brand availability. Whether it then ' +
    'opens depends on its provider — a real integration opens it, and a provider with no ' +
    'integration behind it keeps refusing, with the tile saying which. Enabling is refused ' +
    'outright while the provider itself is not live.',
  MAINTENANCE:
    'Records the game as in maintenance, with your reason, in the configuration log. The player ' +
    'app keeps showing the game and refuses to open it, naming maintenance. No round in progress ' +
    'is closed and no stake is returned — this only stops the NEXT one — and no player is told.',
  DISABLED:
    'Records the game as disabled, with your reason, in the configuration log, and takes it off ' +
    'the player catalogue on every brand it is enabled for. No round in progress is closed — ' +
    'the write touches the game row and never a round — and no player is told: nothing about a ' +
    'status change reaches a player except the game leaving their shelf. Enabling it again puts ' +
    'it back on the same shelves — the brands it is enabled for are remembered.',
};

/** A pending change: what was asked for, before the reason is given. */
interface PendingChange {
  status: GameStatus;
  ids: string[];
  names: string[];
}

/**
 * The row this screen renders — THE GENERATED TYPE, not a second declaration
 * of it.
 *
 * This used to be a hand-rolled interface kept beside the SQL SELECT list, and
 * nothing reconciled the two: that is how `brandCountInScope` shipped on
 * `setGameBrandsResult` but not on this row, and `tsc` stayed green while
 * `row.brandCountInScope` was `undefined` on every render (md-gp-24). The
 * fields, their nullability and what each one means are documented ONCE, on
 * `gameListRow` in packages/contracts/src/lists.ts — read it there, not here,
 * or this file starts drifting from it again the same way.
 *
 * `GameRow` stays as a name because every renderer below already spells it,
 * and `BrandPair` (game-brands.ts) is kept as the dialog's own vocabulary — it
 * is structurally identical to the generated `GameBrandShelfEntry`, so no
 * conversion is needed at the boundary between them.
 */
type GameRow = GameListRow;

/**
 * How long ago, in words an operator reads rather than a timestamp they decode.
 *
 * "2 hours ago" answers the only question this notice is asked — is the shelf
 * current? — where "2026-09-25 22:07" makes the reader do arithmetic against a
 * timezone first. The exact instant is kept in the title for anyone who needs
 * it, which is the one place a machine-readable value belongs on this screen.
 *
 * AN IMPORT FROM THE FUTURE IS SHOWN AS "just now" RATHER THAN "in -3 minutes".
 * Clock skew between this browser and the server is ordinary, and a negative
 * duration reads as a bug in the console rather than a difference of seconds.
 */
function sinceWords(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'at an unreadable time';
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 90) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${String(minutes)} minutes ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${String(hours)} hours ago`;
  const days = Math.round(hours / 24);
  return `${String(days)} days ago`;
}

/**
 * When the hourly catalogue import last COMPLETED, and what it did.
 *
 * Null means it has never completed on this deployment — which also covers a
 * job that has been failing on every tick, because the row is written when a
 * run finishes. The notice says that in words rather than rendering a null as
 * "never run".
 */
interface CatalogueImport {
  job: string;
  finished_at: string;
  ok: boolean;
  affected: number | null;
  summary: string | null;
  error: string | null;
}

interface ProviderRow {
  id: string;
  code: string;
  name: string;
  status: string;
  driver: string;
  /**
   * The two columns this tab reported as "missing, not blank" until
   * 690_catalogue_writes.sql. NULL IS A REAL ANSWER ON BOTH and must survive the
   * wire as null: "nobody has recorded a certification date" is a different fact
   * from any date, and "0% of GGR" is a real commercial term that must not be
   * how the screen renders "we were never told".
   */
  certified_at: string | null;
  revenue_share_bp: number | null;
  game_count: string;
  in_maintenance: string;
}

/**
 * Which table a log row came from.
 *
 * Its own type so every place that reads it can be made EXHAUSTIVE. Adding a
 * member here should break the build in each renderer rather than silently
 * routing the new kind into some other kind's label — which is exactly what
 * happened when BRAND arrived and the sub-line was a two-way ternary with an
 * else: a brand row was confidently labelled "Game", with the brand invisible.
 */
type ChangeKind = 'GAME' | 'PROVIDER' | 'BRAND' | 'GAME_CONFIG' | 'PROVIDER_CONFIG';

interface ChangeRow {
  id: string;
  /** Which table the row came from. A provider row under a "Game" column is a lie. */
  kind: ChangeKind;
  /** The title's name, or the provider's. A BRAND row names the TITLE. */
  subject: string;
  /** The provider's code, and NULL for a title — whose name is unique here. */
  subject_code: string | null;
  /**
   * The brand's NAME, non-null only on a BRAND row.
   *
   * Its own field rather than borrowed space in `subject_code`, which means
   * "the code this subject is read by" and holds a provider's. One field
   * meaning two things depending on a sibling column is how a brand ends up
   * rendered under a provider heading.
   */
  brand: string | null;
  /**
   * WHICH FIELD moved — non-null only on the two CONFIG kinds.
   *
   * Its own field for exactly the reason `brand` has one: `subject_code` means
   * "the code this subject is read by" and holds a provider's code. One field
   * meaning two things depending on a sibling column is how a brand ended up
   * rendered under a provider heading, which is the defect that shape was
   * rewritten to remove.
   */
  field: string | null;
  /**
   * FREE TEXT ON THE TWO CONFIG KINDS, and a status word on the other three.
   *
   * A config row carries a name, a category, an integer or a date here — never a
   * member of the LIVE/MAINTENANCE/DISABLED vocabulary. That is why the
   * transition is rendered through a PER-KIND word map that leaves an unknown
   * value alone: looking '1250' up in a status vocabulary renders an empty cell.
   *
   * Nullable on the config kinds: a field that was not previously set has no
   * `from`, and a cleared field has no `to`.
   */
  from_status: string | null;
  to_status: string | null;
  reason: string;
  changed_by: string | null;
  created_at: string;
}

/* ── Filters ───────────────────────────────────────────────────────────────
   The chip row deliberately mixes two dimensions against one control: three
   status values and two categories. That is the design's set, and it is
   single-select, so "Live" and "Slots" can never be combined — a row counts for
   a chip when its status OR its category matches. */
const FILTERS = ['All', 'Live', 'Maintenance', 'Disabled', 'Slots', 'Live casino'] as const;
type GameFilter = (typeof FILTERS)[number];

const matchesFilter = (row: GameRow, filter: GameFilter): boolean =>
  filter === 'All' ||
  STATUS_LABEL[row.status] === filter ||
  CATEGORY_LABEL[row.category] === filter;

type SortKey = 'name' | 'rtp' | 'rounds' | 'ggr';
type TabKey = 'games' | 'providers' | 'transactions' | 'reconciliation' | 'log';

/**
 * "All 3" / "FairPlay, Jackpot" / "—".
 *
 * Derived, never stored: the API hands back full brand names joined with a
 * comma, and the design's short form is first-word-only.
 */
/**
 * The brand cell.
 *
 * THE COUNT IS NOW EXACT. It used to be `row.brands.split(', ').length`, so a
 * brand named `"Acme, Inc"` counted as two and a game on 2 of 3 brands
 * rendered `All 3` — a confident false statement about coverage, on the
 * column whose entire job is reporting coverage. Counting entries in a set
 * cannot go wrong that way; there is nothing to parse.
 *
 * `enabled` is filtered rather than assumed: a pair with no row in
 * `game_brand` is ABSENT from the array, and never-considered is not the same
 * fact as switched-off, so a present-but-false entry must not be counted as
 * carrying the title.
 */
function brandSummary(row: GameRow): string {
  const on = row.brandShelfInScope.filter((e) => e.enabled);
  if (on.length === 0) return '—';

  /* Both numbers come from one row of one query. Nothing here is joined
     against a second request. */
  const everything = row.brandCountInScope > 0 && on.length >= row.brandCountInScope;

  /**
   * THE BARE "All N" IS RETIRED, and the reason is worth more than the words.
   *
   * It was TRUE and it READ as "all brands on the platform". For an operator
   * scoped to one brand it said "All 1" — accurate, and the most misleading
   * thing on the screen, to the person least able to check it. No arithmetic
   * repaired that, because the arithmetic was already right.
   *
   * At ONE brand the short form is not even short: the brand's NAME is fewer
   * characters than "All 1" and says something. It removes the reading
   * instead of qualifying it.
   *
   * Above one, "in scope" carries the qualification WITHOUT needing to know
   * the platform total. "All N of yours" would have required knowing whether
   * the caller's scope is narrower than the platform — a second source, and
   * the exact defect `brandCountInScope` exists to remove, reintroduced one
   * word later.
   *
   * No caller sees the bare form again, including an unscoped Super Admin for
   * whom it was fine: a phrase that is only safe for some readers is a phrase
   * that will be read by the others.
   */
  if (everything) {
    return row.brandCountInScope === 1
      ? (on[0]?.brand ?? '—')
      : `All ${row.brandCountInScope} in scope`;
  }
  /* The FIRST WORD, and the trailing punctuation goes with the space that
     used to delimit it. `"Acme, Inc".split(' ')[0]` is `"Acme,"` — comma
     included — so joining those with `', '` rendered `Acme,, Beta` for
     exactly the brand name this whole change exists to handle. Display only,
     never parsed back, so not the information-destroying defect; wrong on the
     one name that matters, which is worse than wrong on an arbitrary one. */
  return on.map((e) => (e.brand.split(' ')[0] ?? e.brand).replace(/[,;]$/, '')).join(', ');
}

const thumbStyle: CSSProperties = {
  width: '28px',
  height: '28px',
  flex: 'none',
  display: 'grid',
  placeItems: 'center',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-tag-accent-bg)',
  color: 'var(--color-tag-accent-fg)',
  fontSize: '15px',
};

const subLine: CSSProperties = { fontSize: '11px' };

/* ── Columns ───────────────────────────────────────────────────────────────
   The games table is a factory because three of its cells depend on live state
   (selection, and which column carries the sort arrow). The other two tables
   are static.

   Sort direction is shown with an arrow GLYPH rather than a tinted header: the
   shared DataTable takes a plain string header, and colour alone would not have
   been allowed to carry the state anyway. */
function gameColumns(opts: {
  selected: Set<string>;
  onToggle: (id: string) => void;
  onOpen: (row: GameRow) => void;
  sortKey: SortKey;
  descending: boolean;
}): Column<GameRow>[] {
  const arrow = (key: SortKey) =>
    opts.sortKey === key ? (opts.descending ? ' ↓' : ' ↑') : '';

  return [
    {
      key: 'select',
      header: 'Select',
      render: (g) => (
        <Choice
          checked={opts.selected.has(g.id)}
          onChange={() => opts.onToggle(g.id)}
          label={<span className="sr-only">Select {g.name}</span>}
        />
      ),
    },
    {
      key: 'game',
      header: `Game${arrow('name')}`,
      render: (g) => (
        <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'nowrap' }}>
          <span aria-hidden="true" style={thumbStyle}>
            {/* `icon_name` is nullable — the type convergence to `GameListRow`
                (md-gp-24) is what surfaced this; the hand-rolled interface it
                replaced said `string` "by luck of the seed" (contracts'
                wording). A generic placeholder for a title with no icon set is
                a display choice, not a fact being invented — unlike a null RTP,
                nothing here is measured or aggregated from this value. */}
            <Icon name={g.icon_name ?? 'game-controller'} />
          </span>
          <span>
            {/* The design opens the drawer from the Game cell specifically, not
                from the whole row — the row also carries a checkbox, and one
                target that does two things is a mis-click waiting to happen. */}
            <Button variant="ghost" small onClick={() => opts.onOpen(g)}>
              {g.name}
            </Button>
            <div className="text-subtle" style={subLine}>
              {/* The code, then " · picture uploaded here" on a title whose picture
                  was uploaded, so the table marks every upload an operator made. */}
              {[g.code, pictureListTag(g)].filter((part) => part !== null).join(' · ')}
            </div>
          </span>
        </div>
      ),
    },
    {
      key: 'provider',
      header: 'Provider',
      render: (g) => (
        <>
          {g.provider}
          {/* A provider that is not live blocks its whole shelf: "a failed test
              blocks enabling any of its games." Worth seeing on the row. */}
          {g.provider_status !== 'LIVE' && (
            <div className="text-subtle" style={subLine}>
              provider {(STATUS_LABEL[g.provider_status] ?? g.provider_status).toLowerCase()}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      secondary: true,
      render: (g) => (
        <span className="text-muted">{CATEGORY_LABEL[g.category] ?? g.category}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (g) => <Tag tone={statusTone(g.status)}>{STATUS_LABEL[g.status] ?? g.status}</Tag>,
    },
    {
      key: 'rtp',
      header: `RTP${arrow('rtp')}`,
      numeric: true,
      // Provider-declared and read-only. The measured figure lives in the drawer
      // next to it, because the pair is the point, not either number alone.
      //
      // A provider that publishes no per-game figure leaves this null, and
      // `Number(null)` is 0: rendering it unguarded would print "0.0%", which
      // says this game returns NOTHING to the player. That is a claim about a
      // gambling product that no column supports. An absent figure is an em
      // dash, the way every other absent value on this screen is — a PLAIN
      // dash, because the row's other absent values arrive as plain ones from
      // the query and a differently coloured one would read as a different
      // kind of nothing.
      render: (g) =>
        g.declared_rtp_bp === null ? (
          '—'
        ) : (
          <span className="tabular">{formatRate(Number(g.declared_rtp_bp))}</span>
        ),
    },
    {
      key: 'rounds',
      header: `Rounds 30d${arrow('rounds')}`,
      numeric: true,
      render: (g) => <span className="tabular">{formatCount(BigInt(g.rounds))}</span>,
    },
    {
      key: 'ggr',
      header: `GGR 30d${arrow('ggr')}`,
      numeric: true,
      // Compact here, exact in the drawer — the abbreviation is only safe where
      // the full figure is one click away.
      render: (g) => <span className="tabular">{formatPesosCompact(BigInt(g.ggr))}</span>,
    },
    {
      key: 'brands',
      header: 'Brands',
      secondary: true,
      render: (g) => (
        <span className="text-muted">{brandSummary(g)}</span>
      ),
    },
  ];
}

/**
 * The provider table, built with its actions so the buttons can reach the
 * screen's state.
 *
 * `lock` is the reason the controls are disabled, or null when they are live.
 * It is stated on the button itself rather than left as a dead control: a
 * disabled thing with no explanation is indistinguishable from a broken one.
 */
const providerColumns = (opts: {
  lock: string | null;
  onChange: (provider: ProviderRow, to: ProviderStatus) => void;
  onEdit: (provider: ProviderRow) => void;
}): Column<ProviderRow>[] => [
  {
    key: 'name',
    header: 'Provider',
    render: (p) => (
      <>
        {p.name}
        <div className="text-subtle" style={subLine}>
          {p.code}
        </div>
      </>
    ),
  },
  {
    key: 'games',
    header: 'Games',
    numeric: true,
    // COUNT(*) across ALL brands, unlike the list screen, which is brand-scoped.
    render: (p) => <span className="tabular">{formatCount(Number(p.game_count))}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    render: (p) => {
      const maintaining = Number(p.in_maintenance);
      return (
        <>
          <Tag tone={statusTone(p.status)}>{STATUS_LABEL[p.status] ?? p.status}</Tag>
          {maintaining > 0 && (
            <div className="text-subtle" style={subLine}>
              {maintaining} in maintenance
            </div>
          )}
        </>
      );
    },
  },
  {
    key: 'driver',
    header: 'Driver',
    // Not in the design's column list, but it is the one integration fact the
    // schema actually holds — and "a MOCK driver is refused in production", so
    // an operator planning a launch needs to see it.
    render: (p) => <Tag tone={p.driver === 'MOCK' ? 'outline' : 'neutral'}>{p.driver}</Tag>,
  },
  {
    key: 'integration',
    header: 'Certified',
    /* The design's Integration column, drawn at last. NULL RENDERS AS A WORD:
       "Not recorded" and a date are different facts, and an empty cell reads as
       a loading state. The date is Manila-local because that is the calendar
       every other date on this console is read in. */
    render: (p) =>
      p.certified_at === null ? (
        <span className="text-subtle">Not recorded</span>
      ) : (
        <>
          {new Date(`${p.certified_at}T00:00:00+08:00`).toLocaleDateString('en-PH', {
            dateStyle: 'medium',
          })}
          {/* RECORDED, NOT ENFORCED — said on the row and not only in the notice
              above, because this is the cell an operator reads when they are
              deciding whether a shelf is safe to open. */}
          <div className="text-subtle" style={subLine}>
            Recorded, not enforced
          </div>
        </>
      ),
  },
  {
    key: 'revenue',
    header: 'Revenue share',
    numeric: true,
    /* Basis points to a percentage, and 0% IS A FIGURE. A provider that costs
       nothing is a real term, so it must not render the way "we were never told"
       does — which is why the null branch is a word rather than a dash. */
    render: (p) =>
      p.revenue_share_bp === null ? (
        <span className="text-subtle">Not recorded</span>
      ) : (
        <span className="tabular">{`${String(Number((p.revenue_share_bp / 100).toFixed(2)))}% of GGR`}</span>
      ),
  },
  {
    key: 'record',
    header: 'Record',
    render: (p) => (
      <div className="stack" style={{ gap: 'var(--space-1)' }}>
        <Button
          onClick={() => opts.onEdit(p)}
          disabled={opts.lock !== null}
          aria-label={`Edit ${p.name}'s certification and revenue share`}
        >
          Edit record
        </Button>
        {/* WHY IT IS OFF, AS VISIBLE TEXT rather than a title attribute. The
            status buttons in the next column state their reason in a `title`
            and lean on the notice above the table; that was the older
            convention and this column does not follow it, because a reason
            only a mouse can reach is a reason a keyboard user never gets. */}
        {opts.lock !== null && (
          <div className="text-subtle" style={subLine}>
            {opts.lock}
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'actions',
    header: 'Status change',
    render: (p) => (
      <div className="row" style={{ gap: 'var(--space-1)', flexWrap: 'wrap' }}>
        {(['LIVE', 'MAINTENANCE', 'DISABLED'] as const).map((to) => {
          const current = p.status === to;
          /* The move it is already on is not an action. The route refuses it
             with a 409 anyway; offering it would be a button whose only
             outcome is an error modal. */
          /* The NAME says what the button does and never changes with state —
             a control that renames itself when it is disabled is one a screen
             reader user cannot find twice. WHY it is off goes in the title,
             which is read as the description, and in the notice above the
             table for anyone who never hovers. */
          const why = current
            ? `${p.name} is already ${(STATUS_LABEL[to] ?? to).toLowerCase()}`
            : opts.lock;
          return (
            <Button
              key={to}
              small
              {...(to === 'LIVE' ? { variant: 'primary' as const } : {})}
              disabled={current || opts.lock !== null}
              aria-label={`${PROVIDER_ACTION_LABEL[to]} ${p.name}`}
              {...(why ? { title: why } : {})}
              onClick={() => opts.onChange(p, to)}
            >
              {PROVIDER_ACTION_LABEL[to]}
            </Button>
          );
        })}
      </div>
    ),
  },
];

/**
 * What each kind says under the subject, and which vocabulary its transition
 * speaks — both keyed by kind, so TypeScript requires a case for every member.
 *
 * A `Record<ChangeKind, …>` rather than a ternary chain on purpose: when a
 * fourth kind arrives it must FAIL TO COMPILE here, not fall into the last
 * `else` and be labelled as something it is not. The row is the operator's only
 * account of who changed what, so a mislabelled one is worse than a missing
 * one — it answers the question wrongly instead of not answering it.
 */
const SUBLINE: Record<ChangeKind, (row: ChangeRow) => string> = {
  GAME: () => 'Game',
  PROVIDER: (row) => `Provider · ${row.subject_code ?? '—'}`,
  BRAND: (row) => `Brand · ${row.brand ?? '—'}`,
  /* The FIELD is what the operator came for on these two, so it leads the
     sub-line. `fieldLabel` names an unknown key rather than swallowing it: a
     field this console has not caught up with must still appear as a word. */
  GAME_CONFIG: (row) => `Game · ${fieldLabel(row.field ?? '—')}`,
  PROVIDER_CONFIG: (row) =>
    `Provider · ${row.subject_code ?? '—'} · ${fieldLabel(row.field ?? '—')}`,
};

/**
 * EMPTY MAPS ON THE TWO CONFIG KINDS, and that is the correct entry rather than
 * a missing one.
 *
 * The renderer reads `words[value] ?? value`, so an empty map passes the stored
 * value through untouched — which is what a name, a category or an integer
 * needs. Pointing these at STATUS_LABEL would look harmless and render '1250' as
 * blank, because a status vocabulary has no entry for it.
 */
const NO_VOCABULARY: Record<string, string> = {};

const TRANSITION_WORDS: Record<ChangeKind, Record<string, string>> = {
  GAME: STATUS_LABEL,
  PROVIDER: STATUS_LABEL,
  BRAND: AVAILABILITY_LABEL,
  GAME_CONFIG: NO_VOCABULARY,
  PROVIDER_CONFIG: NO_VOCABULARY,
};

const TRANSITION_TONE: Record<ChangeKind, (value: string) => TagTone> = {
  GAME: statusTone,
  PROVIDER: statusTone,
  BRAND: availabilityTone,
  /* A config value carries no good/bad reading — 96.5% is not 'better' than 96%
     and this console must not imply one. Quiet on both ends. */
  GAME_CONFIG: () => 'quiet',
  PROVIDER_CONFIG: () => 'quiet',
};

const changeColumns: Column<ChangeRow>[] = [
  {
    key: 'when',
    header: 'When',
    render: (c) => (
      <span className="text-muted">
        {new Date(c.created_at).toLocaleString('en-PH', {
          dateStyle: 'medium',
          timeStyle: 'short',
        })}
      </span>
    ),
  },
  {
    key: 'subject',
    header: 'What changed',
    /* One list, two kinds. The kind is said in WORDS beside the name rather
       than implied by an icon or a colour: an operator reading "SA Gaming"
       cannot otherwise tell whether a title or the whole shelf behind it
       moved, and that difference is usually the answer they came for. */
    render: (c) => (
      <>
        {c.subject}
        <div className="text-subtle" style={subLine}>
          {SUBLINE[c.kind](c)}
        </div>
      </>
    ),
  },
  {
    key: 'transition',
    header: 'Change',
    render: (c) => {
      /* Two vocabularies in one column, chosen by KIND rather than guessed from
         the value: a brand row says on/off/not set, a status row says
         live/maintenance/disabled. Sharing one map would let a brand row's word
         be read as a claim about the game itself. */
      const words = TRANSITION_WORDS[c.kind];
      /* NULL IS RENDERED AS A WORD on both ends. A config row whose field was
         not previously set has no `from`, and one whose field was CLEARED has no
         `to` — two facts an empty Tag cannot tell apart, and "Not recorded" is
         the same wording the Providers tab uses for the same state. Basis points
         are shown as the percentage that was typed, chosen by FIELD: two fields
         are basis points and they mean different things. A config row's null end
         goes through the same field formatter, because the picture's null is not
         "not recorded" but a real state — no picture, the tile shows initials —
         and every other field's null still reads "Not recorded" there. */
      const render = (value: string | null): string => {
        if (value === null) return c.field ? formatFieldValue(c.field, null) : 'Not recorded';
        return words[value] ?? (c.field ? formatFieldValue(c.field, value) : value);
      };
      return (
        <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'nowrap' }}>
          <Tag tone="quiet">{render(c.from_status)}</Tag>
          <span aria-hidden="true">{'→'}</span>
          <Tag tone={c.to_status === null ? 'outline' : TRANSITION_TONE[c.kind](c.to_status)}>
            {render(c.to_status)}
          </Tag>
        </span>
      );
    },
  },
  {
    key: 'reason',
    /* reason is NOT NULL in game.game_status_change — "a status change requires
       a reason" is enforced by the schema, so a writer that bypasses the API
       entirely still cannot record a change without one.

       One column holds two things: the route stores "CODE — note", so the code
       can be grouped by a report and the note read by a person. Split back apart
       here. A row with no separator is a seeded entry written before the codes
       existed, and is shown as the prose it is rather than mislabelled. */
    header: 'Reason',
    render: (c) => {
      const cut = c.reason.indexOf(GAME_REASON_SEPARATOR);
      if (cut < 0) return <span className="text-muted">{c.reason}</span>;
      return (
        <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'nowrap' }}>
          <Tag tone="quiet">
            {logReasonLabel(c.reason.slice(0, cut))}
          </Tag>
          <span className="text-muted">
            {c.reason.slice(cut + GAME_REASON_SEPARATOR.length)}
          </span>
        </span>
      );
    },
  },
  {
    key: 'by',
    header: 'Changed by',
    secondary: true,
    render: (c) => c.changed_by ?? <span className="text-subtle">system</span>,
  },
];

export function Games() {
  const { session, brandId, setBrandId, brands, allowed } = useSession();
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  /*
   * The catalogue, the providers and the configuration log are all read behind players.view. A
   * role without it — the PAGCOR Regulator — is refused below, and both reading effects ask this
   * first: the refusal is a render, and an effect registered above it would still fetch on mount.
   */
  const canReadPlayers = allowed('players.view');

  const [games, setGames] = useState<GameRow[]>([]);
  /* Whether this installation has a picture store. Off until the catalogue says
     so: an Upload button that is on and then refuses is worse than one that waits. */
  const [pictureUploadsReady, setPictureUploadsReady] = useState(false);
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [changes, setChanges] = useState<ChangeRow[]>([]);
  const [catalogueImport, setCatalogueImport] = useState<CatalogueImport | null>(null);
  /* Every provider's last catalogue import, by provider code; null when that job never finished. */
  const [catalogueImports, setCatalogueImports] = useState<Record<string, CatalogueImport | null>>({});
  /* Distinguishes 'the import has never completed' from 'we have not asked yet'. */
  const [importRead, setImportRead] = useState(false);
  const [loading, setLoading] = useState(true);
  const [referenceLoading, setReferenceLoading] = useState(true);

  const [tab, setTab] = useState<TabKey>('games');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<GameFilter>('All');
  const [sortKey, setSortKey] = useState<SortKey>('ggr');
  const [descending, setDescending] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detail, setDetail] = useState<GameRow | null>(null);
  /*
   * Whether the last read FAILED, as distinct from returning nothing.
   *
   * Without this the empty state said "This is a real zero — /api/games
   * returned no rows" after a request that never returned at all. The operator
   * dismisses the error modal, reads the table, and is told the catalogue is
   * empty. "We have no games" and "we could not ask" are different facts and
   * only one of them is safe to act on.
   */
  const [loadFailed, setLoadFailed] = useState(false);

  /* ── The write ────────────────────────────────────────────────────────
     A change is asked for by a button, and confirmed in a dialog that collects
     the reason. Nothing is sent until both halves exist. */
  const [pending, setPending] = useState<PendingChange | null>(null);
  /* The provider write has its own pending value and its own dialog: a
     different route, a different capability and a different reason set. */
  const [pendingProvider, setPendingProvider] = useState<PendingProviderChange | null>(null);
  /* The four catalogue writes. Each dialog holds its own form state; the screen
     holds only "which one is open", and the last result so the sentence under
     the table can say what happened after the re-read replaces the rows. */
  const [addingGame, setAddingGame] = useState(false);
  const [addingProvider, setAddingProvider] = useState(false);
  const [editingGame, setEditingGame] = useState<PendingGameConfig | null>(null);
  const [editingProvider, setEditingProvider] = useState<PendingProviderConfig | null>(null);
  const [lastCatalogueWrite, setLastCatalogueWrite] = useState<{
    sentence: string;
    writes: number;
  } | null>(null);
  /* The after-count describes the state at ONE write, so it is stamped with
     the write it belongs to. Any later write on this screen — another provider,
     or a game moved on the Games tab — bumps the counter and the sentence stops
     being shown rather than quietly describing a shelf that has since changed.
     The durable record is the provider log; this is only the moment. */
  const [lastProviderChange, setLastProviderChange] = useState<{
    result: ChangeProviderStatusResult;
    writes: number;
  } | null>(null);
  /* The per-brand write has its own pending value for the same reasons: its
     own route, its own three-code reason set, and its own result sentence. */
  const [pendingBrands, setPendingBrands] = useState<PendingBrandChange | null>(null);
  /* The bulk half of brand availability: one chip set, many titles. */
  const [bulkBrands, setBulkBrands] = useState(false);
  const [lastShelfChange, setLastShelfChange] = useState<{
    result: SetGameBrandsResult;
    writes: number;
  } | null>(null);
  const [reasonCode, setReasonCode] = useState<GameStatusChangeReason>('SCHEDULED_MAINTENANCE');
  const [note, setNote] = useState('');
  /* The note is only marked wrong once the operator has been in the box. An
     error shown before anyone has typed reads as a failure rather than a rule. */
  const [noteTouched, setNoteTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /**
   * Bumped after every successful write, so BOTH reads re-run.
   *
   * A status change moves more than the row it names: the chip counts, the
   * provider tab's "in maintenance" tally and the configuration log all change
   * with it. Patching the row in place would leave three of those four stale.
   */
  const [writeCount, setWriteCount] = useState(0);

  /* The catalogue itself is brand-scoped server-side: the round counts, the GGR
     and the enabled-brand list are all narrowed by brandId. */
  useEffect(() => {
    if (!canReadPlayers) return;
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (brandId) params.set('brandId', brandId);
    const qs = params.toString();

    api
      .get<GameListResult>(`/api/games${qs ? `?${qs}` : ''}`)
      .then((d) => {
        if (cancelled) return;
        setGames(d.items);
        setPictureUploadsReady(d.pictureUploadsReady);
        setLoadFailed(false);
        // A scope change re-cuts the list; a selection carried over it would
        // let an operator act on rows they can no longer see.
        setSelected(new Set());
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setGames([]);
        setLoadFailed(true);
        showError({
          title: 'Could not load the game catalogue',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [canReadPlayers, brandId, showError, writeCount]);

  /* Providers and the configuration log are platform-wide — neither endpoint
     takes brandId — so they load once rather than on every scope change. */
  useEffect(() => {
    if (!canReadPlayers) return;
    let cancelled = false;
    setReferenceLoading(true);

    Promise.all([
      api.get<{
        items: ProviderRow[];
        catalogueImport: CatalogueImport | null;
        catalogueImports?: Record<string, CatalogueImport | null>;
      }>(
        '/api/games/providers',
      ),
      api.get<{ items: ChangeRow[] }>('/api/games/changes'),
    ])
      .then(([p, c]) => {
        if (cancelled) return;
        setProviders(p.items);
        setCatalogueImport(p.catalogueImport);
        setCatalogueImports(p.catalogueImports ?? {});
        setImportRead(true);
        setChanges(c.items);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        showError({
          title: 'Could not load providers and the configuration log',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => !cancelled && setReferenceLoading(false));

    return () => {
      cancelled = true;
    };
  }, [canReadPlayers, showError, writeCount]);

  const brandLabel = brandId
    ? (brands.find((b) => b.id === brandId)?.name ?? 'Selected brand')
    : brands.length === 1
      ? brands[0]!.name
      : `All ${brands.length || ''}`.trim();

  /* Brand scope, then search, then chip — in that order, so the chip counts
     describe exactly the set a click would produce.

     Under a single-brand scope the API still RETURNS every game; it only
     narrows the shelf. So an empty shelf under a scope means "not enabled on
     this brand", and that is the filter the design describes.

     `some(enabled)` rather than a length test: the array carries pairs that
     exist but are switched off, and a game switched off on the selected brand
     is not in scope for this view. Length alone would let it through. */
  const inScope = useMemo(
    () => (brandId ? games.filter((g) => g.brandShelfInScope.some((e) => e.enabled)) : games),
    [games, brandId],
  );

  const searched = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return inScope;
    return inScope.filter((g) =>
      `${g.name} ${g.provider} ${g.code}`.toLowerCase().includes(needle),
    );
  }, [inScope, query]);

  const filtered = useMemo(() => searched.filter((g) => matchesFilter(g, filter)), [searched, filter]);

  /* A selection may never outlive the rows it was made on.
     The brand scope already clears it above, for exactly that reason — but the
     search box and the chip row re-cut the list in precisely the same way and
     were not doing the same thing. Selecting all eight games and then clicking
     "Maintenance" left the bulk bar reading "8 selected" over a single row, and
     a search matching nothing left it reading "8 selected" over the empty
     state. That is the failure the write path would inherit: the reason bulk
     actions are scoped to the FILTERED rows in the first place.
     Pruned rather than cleared, so narrowing a search one keystroke at a time
     does not throw away a selection whose rows are all still on screen. */
  useEffect(() => {
    setSelected((current) => {
      if (current.size === 0) return current;
      const visible = new Set(filtered.map((g) => g.id));
      const next = new Set([...current].filter((id) => visible.has(id)));
      // Same set, same reference: an unconditional new Set would re-render on
      // every sort and every keystroke.
      return next.size === current.size ? current : next;
    });
  }, [filtered]);

  const sorted = useMemo(() => {
    const direction = descending ? -1 : 1;
    // Number() is safe on these keys: basis points and round counts are small,
    // and GGR in centavos stays far inside Number.MAX_SAFE_INTEGER. Declared RTP
    // is the one that can be absent, and absent is not a number — see below.
    return filtered.toSorted((a, b) => {
      switch (sortKey) {
        case 'name':
          return a.name.localeCompare(b.name) * direction;
        case 'rtp': {
          // A game whose provider publishes no RTP has no place on the scale, so
          // it sorts LAST in BOTH directions rather than as a zero. Sorting it
          // as zero would rank it as the worst-returning game on the shelf,
          // which is the same lie the column used to print.
          const left = a.declared_rtp_bp === null ? null : Number(a.declared_rtp_bp);
          const right = b.declared_rtp_bp === null ? null : Number(b.declared_rtp_bp);
          if (left === null || right === null) {
            if (left === right) return 0;
            return left === null ? 1 : -1;
          }
          return (left - right) * direction;
        }
        case 'rounds':
          return (Number(a.rounds) - Number(b.rounds)) * direction;
        default:
          return (Number(a.ggr) - Number(b.ggr)) * direction;
      }
    });
  }, [filtered, sortKey, descending]);

  const chipOptions = useMemo(
    () =>
      FILTERS.map((key) => ({
        key,
        label: key,
        count: key === 'All' ? searched.length : searched.filter((g) => matchesFilter(g, key)).length,
      })),
    [searched],
  );

  const columns = useMemo(
    () =>
      gameColumns({
        selected,
        onToggle: (id) =>
          setSelected((current) => {
            const next = new Set(current);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          }),
        onOpen: setDetail,
        sortKey,
        descending,
      }),
    /* `brands.length` was here for `totalBrands`, which the column factory no
       longer takes — the denominator travels on the row now. A dependency
       kept past the value it fed rebuilds the columns for a reason that no
       longer exists. */
    [selected, sortKey, descending],
  );

  /* Select-all covers the FILTERED rows only, and reads as checked only when
     every one of them is selected — otherwise "select all" on a searched list
     silently arms rows the operator cannot see. */
  const allSelected = sorted.length > 0 && sorted.every((g) => selected.has(g.id));

  /* The rows behind the selection, in the order they are on screen. Taken from
     `sorted` rather than `games` because the selection is already pruned to the
     visible rows — a bulk must act on exactly what the bar is counting. */
  const selectedRows = useMemo(() => sorted.filter((g) => selected.has(g.id)), [sorted, selected]);

  /** Ask for a change. Nothing is sent until the dialog has a reason and a note. */
  const askToChange = useCallback((status: GameStatus, rows: GameRow[]) => {
    if (rows.length === 0) return;
    setPending({ status, ids: rows.map((g) => g.id), names: rows.map((g) => g.name) });
    setReasonCode(DEFAULT_REASON[status]);
    setNote('');
    setNoteTouched(false);
  }, []);

  /**
   * Send it.
   *
   * The route applies the whole set in one transaction, so there is no partial
   * outcome to report: either every game named moved, or none did and the
   * refusal says which rule stopped it. Failures open the modal — a toast can be
   * missed, and an operator who misses "the game did NOT go dark" acts on a
   * false belief.
   */
  const submitChange = useCallback(async () => {
    if (!pending) return;
    setSubmitting(true);
    try {
      const result = await api.post<ChangeGameStatusResult>('/api/games/status', {
        gameIds: pending.ids,
        status: pending.status,
        reasonCode,
        note: note.trim(),
      });
      const label = (STATUS_LABEL[result.status] ?? result.status).toLowerCase();
      toast(
        result.count === 1
          ? `${result.changed[0]?.name ?? 'The game'} is now ${label}. The reason is in the configuration log.`
          : `${result.count} games are now ${label}. The reason is in the configuration log.`,
      );
      setPending(null);
      setNote('');
      setDetail(null);
      setWriteCount((n) => n + 1);
    } catch (error: unknown) {
      showError({
        title:
          pending.ids.length === 1
            ? `${pending.names[0] ?? 'The game'} was not changed`
            : `None of the ${pending.ids.length} games were changed`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was written. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSubmitting(false);
    }
  }, [pending, reasonCode, note, showError, toast]);

  const noteError =
    noteTouched && note.trim().length < 10
      ? 'At least 10 characters. This is what an operator reads six months from now.'
      : undefined;


  /**
   * TWO WALLS, NOT ONE, AND EVERY WRITE ON THIS SCREEN NOW MEETS BOTH.
   *
   * `games.manage` is declared `requiresTwoFactor`, and `assertCan` enforces
   * the second factor PER KEY against THIS SESSION — a role can hold the key
   * while the session that holds the role never presented a second factor, and
   * the route refuses on the session. So holding the key is not enough to
   * predict that a write will be accepted.
   *
   * THIS FIXES A DEFECT THAT EXISTS ON DEVELOP TODAY — it is not fallout from
   * the gate move. `access.manage` is declared `requiresTwoFactor: true`
   * (rbac/src/index.ts:25) exactly as `games.manage` is (:245), so the status
   * controls were ALREADY behind a second factor they did not mention, and a
   * key-holder on an unverified session already met a silent 403. Unifying the
   * key did not introduce that; it removed the last excuse for it.
   *
   * The provider and shelf controls have always checked both and named both.
   * The STATUS controls checked only the key, because the sentence beside them
   * was written when they were gated on a different one. Now that all three go
   * through the same key there is nothing left to justify the difference: two
   * controls on one screen, behind one permission, disagreeing about whether
   * the session is part of the question.
   *
   * So the status lock is computed by the same helper. That is a real change in
   * what an operator sees — a key-holder on an unverified session now finds the
   * status buttons disabled with the reason named, where before they were
   * enabled and the write came back 403 with no warning. Disabled-and-explained
   * is the honest one, and it is what the sibling controls already do.
   *
   * `canManage` keeps its type and its meaning, so this is four lines and not
   * a rewrite of the twenty places that read it. (`lockReason` is gone — see
   * `statusLock` below for why the pair became one value.)
   */
  const holdsManageKey = allowed(MANAGE_KEY);

  /**
   * The two walls are shared; the NOUN is not. One changes a provider, one
   * changes which brands carry a title, one changes a game's status. A single
   * string for all of them told an operator in the game drawer that "changing
   * a provider" was refused, which is not what they were trying to do.
   *
   * IF EVERY CONTROL HERE IS LOCKED FOR EVERY OPERATOR INCLUDING SUPER ADMIN,
   * CHECK THAT `packages/db/sql/600_games_manage.sql` HAS RUN BEFORE YOU LOOK
   * AT ANY ROLE. `can()` is plain list membership with no isSuper bypass
   * (rbac/src/index.ts:350), so until that migration grants the key, NOBODY
   * holds it and the route refuses everyone — and the sentence below will say
   * "your role is Super Admin", which reads as a settled policy decision and
   * sends whoever is escalated to straight at the role matrix. The symptom
   * points away from the cause; this comment is here because it is where that
   * person lands. The filename is deliberately NOT in the operator-facing
   * string: an operator cannot act on a migration, and a screen that names one
   * is telling the wrong person.
   */
  const manageLock = (what: string): string | null =>
    !holdsManageKey
      ? /* The role clause is omitted rather than defaulted while the session
           loads: `role` used to fall back to the words "your role", so this
           read "your role is your role" for as long as the request was in
           flight. Two independently-defaulted parts in one sentence. */
        `${what} needs ${MANAGE_KEY}${session?.role ? ` — your role is ${session.role}` : ''}`
      : !(session?.mfa ?? false)
        ? `${MANAGE_KEY} needs a session verified with two-factor authentication — sign in again with your second factor`
        : null;

  /**
   * `statusLock` IS THE VALUE. There is no `lockReason` string beside it any
   * more, and the empty string it used to fall back to is gone.
   *
   * The old pair was `canManage: boolean` plus `lockReason: string`, where
   * `''` meant "no lock". Two facts, one of them derived, and nothing made
   * them agree — the type allowed `canManage` true beside a reason, and it
   * allowed the empty string to reach a sentence that reads `${reason}, so
   * those controls stay disabled for you`, which renders as a fragment
   * beginning with a comma. A comment said the empty string was unreachable.
   * A comment is not a type, and the four places that read it could not see
   * the comment.
   *
   * Now the null IS the "no lock" case, every copy site branches on
   * `statusLock === null`, and TypeScript narrows the other branch to a real
   * string. The sentence cannot be rendered without one.
   *
   * `canManage` stays, derived on one line, because ten controls read it as
   * `disabled={!canManage}` and `!(statusLock !== null)` is worse at every one
   * of them. Derived from the same value on the line below, it cannot drift.
   */
  const statusLock = manageLock('Changing game status');
  const canManage = statusLock === null;
  const providerLock = manageLock('Changing a provider');
  const jiliImport = catalogueImports['JILI'] ?? null;
  /* Null unless the last run finished without failing; the notice's words for that case. */
  const jiliRan = jiliImport?.ok === true ? jiliImportRanWords(jiliImport.summary, sinceWords(jiliImport.finished_at)) : null;
  const shelfLock = manageLock("Changing a title's shelf");

  const gameCountLabel = `${sorted.length} of ${games.length} games`;

  // Below every hook, so the hooks run in the same order for every reader; the
  // reads above are what `canReadPlayers` stops.
  if (!canReadPlayers) {
    return (
      <EmptyState icon="lock-key" title="Game management">
        This module needs the players.view capability. Your role does not hold it.
      </EmptyState>
    );
  }

  return (
    <>
      {/*
        THE SENTENCE UNDER THE NAME IS THE POINT OF THIS HEADER.

        This screen used to open with "Game management" and a count line, above a
        tablist and a stack of notices about what a status does and does not do.
        Every word of that was true and none of it said what the screen was FOR,
        so the person most likely to need telling — somebody sent here once a
        month to take one title off one brand — had to infer the job from the
        figures. The explanation was already written, at length, in the module
        comment at the top of this file, where only a developer would ever read
        it.
      */}
      <ScreenHeader
        title="Game management"
        purpose="For whoever decides which games players can see: turn a title or a whole provider on or off, pick the brands that carry it, and say why."
        /*
          The caption kept exactly as it was, all three parts and both states.

          "Loading…" only while the catalogue has NOTHING yet, which is what
          keeps a re-read after a write from blanking a count the operator is
          mid-sentence about. The providers figure has its own ellipsis because
          it comes from the second, separately-loading read — one number can be
          known while the other is not, and showing a confident "0 providers"
          during that gap would be a claim nobody made.

          The brand scope trails every state because it is true of all of them:
          a reader who does not know whether they are looking at one brand or at
          all of them cannot judge either figure.

          NOTE FOR WHOEVER EXTENDS THIS: unlike the agent and affiliate headers,
          this caption has no read-FAILED state — `loadFailed` is carried on this
          screen but spent on the table's empty state, which is where this file
          draws the "genuinely no games" versus "we could not ask" line. The
          caption was moved here unchanged on purpose rather than quietly grown a
          third state in a commit about headers.
        */
        meta={
          loading && games.length === 0
            ? 'Loading…'
            : `${gameCountLabel} · ${referenceLoading ? '…' : providers.length} providers · ${brandLabel}`
        }
        actions={
          <>
            <Button onClick={() => setTab('providers')}>Providers</Button>
            {/* WIRED, and gated on the SAME wall the status writes are gated on:
                `games.manage` plus a two-factor verified session. The reason it is
                off is stated in the notice below as visible text — `statusLock`
                is the same sentence both controls carry, because it is the same
                wall and two wordings for one wall is how a screen starts
                contradicting itself. */}
            <Button variant="primary" disabled={!canManage} onClick={() => setAddingGame(true)}>
              Add game
            </Button>
          </>
        }
      />

      {/* The design's "Providers" button is a one-way drill-down. A tablist
          gives the same three surfaces and a way back, and keeps the counts
          visible from anywhere. */}
      <Tabs
        label="Game management sections"
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'games', label: 'Games', count: games.length },
          { key: 'providers', label: 'Providers', count: providers.length },
          /* Each of these two is gated on the permission ITS OWN route
             requires: provider-transactions-routes.ts wants players.view and
             the reconciliation routes want transactions.view. They are
             different permissions and one role can hold either without the
             other, so an ungated tab here is a tab that can only error. */
          ...(allowed('players.view')
            ? [{ key: 'transactions' as const, label: 'Provider transactions' }]
            : []),
          ...(allowed('transactions.view')
            ? [{ key: 'reconciliation' as const, label: 'Provider reconciliation' }]
            : []),
          { key: 'log', label: 'Configuration log', count: changes.length },
        ]}
      />

      <div className="notice notice-quiet">
        <Icon name={canManage ? 'notebook' : 'lock-key'} />
        <div>
          <strong>Everything here that changes something asks you why, and keeps your answer.</strong>{' '}
          {statusLock === null
            ? 'You can add a game or a provider, edit either, change a status, and choose which brands carry a title. '
            : `${statusLock}, so every control that changes something stays disabled for you — you can still read the whole screen. `}
          Each change is saved together with your reason, and all of them appear on the{' '}
          <strong>Configuration log</strong> tab. A new provider and a new game both start{' '}
          <strong>disabled</strong>, and a new game is on no brand&rsquo;s shelf — putting something
          in front of players is always a separate step you take on purpose.
        </div>
      </div>

      {/*
        * WHAT JUST HAPPENED, above the tabs rather than inside one.
        *
        * The four catalogue writes are raised from THREE different places — the
        * header's Add game, the Providers tab's Add provider and Edit record, and
        * the drawer's Edit configuration — and two of them close the surface they
        * were raised from. A sentence rendered inside one tab is a sentence the
        * operator is moved away from before they can read it.
        *
        * `writes === writeCount` keys it to the RE-READ, so a stale sentence can
        * never sit above rows that have already moved on past it.
        */}
      {lastCatalogueWrite !== null && lastCatalogueWrite.writes === writeCount && (
        <div className="notice">
          <Icon name="check-circle" />
          <div>{lastCatalogueWrite.sentence}</div>
        </div>
      )}

      {tab === 'games' && (
        <>
          <div className="row">
            <div className="search-field" style={{ flex: '1 1 260px', maxWidth: '420px' }}>
              <Icon name="magnifying-glass" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Game name or provider"
                aria-label="Search games by name, provider or game code"
              />
            </div>
            {/* Sort is a control rather than a clickable header: the shared
                DataTable renders plain string headers, and a table you cannot
                sort from the keyboard is worse than one sorted from a Select. */}
            <Select
              aria-label="Sort games by"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              style={{ maxWidth: '200px' }}
            >
              <option value="ggr">Sort: GGR 30d</option>
              <option value="rounds">Sort: Rounds 30d</option>
              <option value="rtp">Sort: RTP</option>
              <option value="name">Sort: Game</option>
            </Select>
            <Button
              iconOnly
              aria-label={descending ? 'Sort ascending' : 'Sort descending'}
              onClick={() => setDescending((d) => !d)}
            >
              <Icon name={descending ? 'sort-descending' : 'sort-ascending'} />
            </Button>
          </div>

          {lastShelfChange?.writes === writeCount && (
            <div className="notice notice-quiet">
              <Icon name="notebook" />
              <div>
                {shelfSentence(lastShelfChange.result)} The change and your reason are on the
                Configuration log tab.
              </div>
            </div>
          )}

          <Chips
            label="Filter games by status or category"
            options={chipOptions}
            active={filter}
            onChange={setFilter}
          />

          <div className="row">
            <Choice
              checked={allSelected}
              onChange={(e) =>
                setSelected(e.target.checked ? new Set(sorted.map((g) => g.id)) : new Set())
              }
              label={`Select all ${sorted.length} shown`}
            />
          </div>

          {selected.size > 0 && (
            <Panel className="stack">
              <div className="row">
                <strong>{selected.size} selected</strong>
                <Button disabled={!canManage} onClick={() => askToChange('LIVE', selectedRows)}>
                  Enable
                </Button>
                <Button
                  disabled={!canManage}
                  onClick={() => askToChange('MAINTENANCE', selectedRows)}
                >
                  Maintenance
                </Button>
                <Button disabled={!canManage} onClick={() => askToChange('DISABLED', selectedRows)}>
                  Disable
                </Button>
                {/* Availability, not status — a different axis, which is why it
                    is a separate button rather than a fourth status. */}
                <Button disabled={!canManage} onClick={() => setBulkBrands(true)}>
                  Brand availability
                </Button>
                <Button variant="ghost" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
              </div>
              <div className="notice notice-quiet">
                <Icon name={canManage ? 'notebook' : 'lock-key'} />
                <div>
                  {statusLock === null ? (
                    <>
                      All three ask for a reason first and then apply in{' '}
                      <strong>one transaction</strong>: every game named moves, or none does and the
                      refusal says which rule stopped it. There is no partial bulk, and each game
                      is listed separately on the Configuration log tab. What a change does is{' '}
                      <strong>record the status and the reason</strong>, and that status is what
                      the player catalogue reads: a disabled game leaves the shelf and one in
                      maintenance stays visible and refuses to open. No round is closed and no
                      player is notified, and for a narrower reason than this screen used to give:
                      the write touches the game row and never a round, and nothing about a status
                      change reaches a player. It said &ldquo;there are no rounds and nothing
                      messages a player&rdquo;, and both halves had stopped being true &mdash; the
                      Rounds column on this very table counts them, and an announcement send mails
                      its audience for real.
                      Enable is refused for any game whose provider is not live; that rule is
                      enforced, at the moment of the write.
                    </>
                  ) : (
                    <>
                      {statusLock}. Nothing is written and nothing has changed. The selection is
                      real and the counts are real; only the three buttons are out of reach.
                    </>
                  )}
                </div>
              </div>
            </Panel>
          )}

          <Panel>
            {loading && games.length === 0 ? (
              <div className="skeleton" style={{ height: '240px' }}>
                &nbsp;
              </div>
            ) : (
              <DataTable
                caption="Game catalogue with provider, status, declared RTP and 30-day rounds and GGR"
                columns={columns}
                rows={sorted}
                rowKey={(g) => g.id}
                empty={
                  <EmptyState
                    icon="game-controller"
                    title="No games match"
                    action={
                      <Button
                        onClick={() => {
                          // The design's Reset takes the brand scope with it —
                          // a narrow scope is the commonest reason a search
                          // returns nothing, and it is set two headers away.
                          setQuery('');
                          setFilter('All');
                          setBrandId(null);
                        }}
                      >
                        Reset filters
                      </Button>
                    }
                  >
                    {/* The middle branch used to end "— /api/games returned no
                        rows", naming the endpoint (`GET /api/games`, read in the
                        effect above) to an operator who cannot call it, look it
                        up or quote it to anybody. The DISTINCTION it was making
                        is the valuable part and is kept word for word: the
                        catalogue answered, and the answer was nothing. */}
                    {loadFailed
                      ? 'The catalogue could not be loaded, so this table is empty because nothing was read — not because there are no games. Try again shortly.'
                      : games.length === 0
                      ? 'The catalogue is empty in this scope. This is a real zero — the catalogue was read and it came back with no games at all, so nothing here was hidden by a filter.'
                      : `Nothing matches ${query ? `“${query}” ` : ''}under the “${filter}” chip. Search covers game name, provider and game code. Reset clears the search and the chip; the brand scope is set in the header and persists across modules.`}
                  </EmptyState>
                }
              />
            )}
            <div className="pagination">{gameCountLabel}</div>
          </Panel>

          {brandId && (
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                Scoped to <strong>{brandLabel}</strong>: games not enabled on this brand are hidden,
                and Rounds 30d and GGR 30d count only this brand&rsquo;s rounds. The Brands column
                therefore shows one brand by construction — switch the header scope to all brands to
                see real per-brand availability.
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'providers' && (
        <>
          <div className="notice notice-quiet">
            <Icon name="plugs" />
            <div>
              <strong>A provider that is not live blocks every game on its shelf.</strong> You
              cannot set a game live behind one, and a player cannot open one. That rule is really
              enforced, every time. But this status is a <strong>setting, not a signal</strong>: it
              changes only when somebody changes it here, with a reason, and never by itself.
              Nothing is watching the provider, so it will not switch itself over if they go down
              at three in the morning.
            </div>
          </div>

          {/* WHAT DID NOT HAPPEN, kept on the screen rather than in a toast.
              A provider leaving LIVE does not pull its games down, and the
              operator who assumes it did is the one this batch exists for. A
              toast carrying that fact can be missed in the moment it matters. */}
          {lastProviderChange?.writes === writeCount && (
            <div className="notice notice-quiet">
              <Icon name="notebook" />
              <div>
                {afterCountSentence(lastProviderChange.result)} The change and your reason are on
                the Configuration log tab.
              </div>
            </div>
          )}

          {providerLock && (
            <div className="notice notice-quiet">
              <Icon name="lock-key" />
              <div>
                Provider status is read-only for you: {providerLock}. Everything else on this tab
                is a read.
              </div>
            </div>
          )}

          {/*
            * WHEN THE SHELF WAS LAST REFRESHED FROM THE PROVIDER.
            *
            * SA's tables arrive hourly. Without this, a catalogue last updated
            * on Tuesday looks exactly like one updated an hour ago — the same
            * 41 rows, no way to tell. Stated here rather than beside each row
            * because it is one fact about the whole import, not per provider.
            */}
          {importRead && (
            <div className={catalogueImport?.ok === false ? 'notice' : 'notice notice-quiet'}>
              <Icon
                name={
                  catalogueImport === null
                    ? 'warning-circle'
                    : catalogueImport.ok
                      ? 'arrows-clockwise'
                      : 'warning-circle'
                }
              />
              <div>
                {catalogueImport === null ? (
                  <>
                    <strong>The SA Gaming catalogue import has never finished here.</strong> It is
                    meant to run every hour and add any new table SA publishes. Nothing below came
                    from it, and a table SA added today would not be on this list. That is either an
                    import that is not running or one failing on every attempt — both look the same
                    from here, because the record is written when a run finishes.
                  </>
                ) : catalogueImport.ok ? (
                  <>
                    <strong>SA Gaming&rsquo;s tables were last refreshed {sinceWords(catalogueImport.finished_at)}.</strong>{' '}
                    {catalogueImport.summary ?? 'The import completed.'} It runs every hour; new
                    tables arrive <strong>disabled</strong> and on no brand&rsquo;s shelf, so
                    nothing reaches a player until somebody puts it there.
                  </>
                ) : (
                  <>
                    <strong>
                      The SA Gaming catalogue import failed {sinceWords(catalogueImport.finished_at)}.
                    </strong>{' '}
                    The list below is as it stood before that, so a table SA has added or renamed
                    since may be missing or wrong here. The provider said:{' '}
                    <em>{catalogueImport.error ?? 'no reason was recorded'}</em>
                  </>
                )}
              </div>
            </div>
          )}

          {importRead && providers.some((p) => p.code === 'JILI') && (
            <div className={jiliImport?.ok === false ? 'notice' : 'notice notice-quiet'}>
              <Icon name={jiliImport?.ok === true ? 'arrows-clockwise' : 'warning-circle'} />
              <div>
                {jiliImport === null ? (
                  <>
                    <strong>The JILI catalogue import has never finished here.</strong> It is meant to
                    run every hour and add any new game JILI lists. Nothing from JILI below came from
                    it, and a game JILI added today would not be on this list. That is either an
                    import that is not running or one failing on every attempt — both look the same from
                    here, because the record is written when a run finishes.
                  </>
                ) : jiliRan ? (
                  <>
                    <strong>{jiliRan.lead}</strong> {jiliRan.summary} It runs every hour; new games
                    arrive <strong>disabled</strong> and on no brand&rsquo;s shelf, so nothing reaches a
                    player until somebody puts it there.
                  </>
                ) : (
                  <>
                    <strong>
                      The JILI catalogue import failed {sinceWords(jiliImport.finished_at)}.
                    </strong>{' '}
                    The list below is as it stood before that, so a game JILI has added or renamed
                    since may be missing or wrong here. The provider said:{' '}
                    <em>{jiliImport.error ?? 'no reason was recorded'}</em>
                  </>
                )}
              </div>
            </div>
          )}

          <Panel
            title="Game providers"
            actions={
              <Button
                variant="primary"
                disabled={!canManage}
                onClick={() => setAddingProvider(true)}
              >
                Add provider
              </Button>
            }
          >
            {referenceLoading && providers.length === 0 ? (
              <div className="skeleton" style={{ height: '200px' }}>
                &nbsp;
              </div>
            ) : (
              <DataTable
                caption="Game providers with game counts, status and integration driver"
                columns={providerColumns({
                  lock: providerLock,
                  onEdit: (p) =>
                    setEditingProvider({
                      id: p.id,
                      code: p.code,
                      name: p.name,
                      certifiedAt: p.certified_at,
                      revenueShareBp: p.revenue_share_bp,
                    }),
                  onChange: (p, to) =>
                    setPendingProvider({
                      id: p.id,
                      name: p.name,
                      from: p.status as ProviderStatus,
                      to,
                    }),
                })}
                rows={providers}
                rowKey={(p) => p.id}
                empty={
                  <EmptyState icon="plugs" title="No providers configured">
                    No provider has been added yet. Every game belongs to a provider, so the whole
                    catalogue stays empty until there is one.
                  </EmptyState>
                }
              />
            )}
          </Panel>

          {/* Rule 7, and the two columns it used to refuse to draw now exist.
              What has NOT changed is that neither ENFORCES anything, which is
              the half an operator would otherwise assume. */}
          <div className="notice notice-quiet">
            <Icon name="warning-circle" />
            <div>
              <strong>Certified and Revenue share are recorded, not enforced.</strong> Edit record
              saves both, with your reason, and they appear on the Configuration log tab. Neither
              one blocks anything: a missing certification date does not stop a game opening, and
              nothing has been billed against the revenue share. If you want a failed certification
              to close a shelf, do it deliberately — set the provider&rsquo;s status, in the column
              beside these. Nothing on this platform monitors a provider&rsquo;s health, so these
              figures are only ever as current as the last person who typed them.
            </div>
          </div>
        </>
      )}

      {/* Both bodies carry the permission their own tab is gated on, so neither
          can render from a tab key the tablist would not have offered. Neither
          is reachable today — `tab` starts at 'games' and its only writers are
          the Providers button and this tablist's onChange — but unreachable is
          a property of today's code, not of the screen: the first deep link to
          a finding, remembered tab or notification that opens one of these
          turns it reachable, and nobody comes back to this file for it. */}
      {tab === 'transactions' && allowed('players.view') && <ProviderTransactions />}

      {tab === 'reconciliation' && allowed('transactions.view') && <ProviderReconciliation />}

      {tab === 'log' && (
        <>
          <div className="notice notice-quiet">
            <Icon name="notebook" />
            <div>
              Every status change carries a mandatory reason and is appended here — titles and
              PROVIDERS in one list, newest first, because an operator asking why something went
              dark at 02:10 does not know in advance whether the answer is the title or the shelf
              behind it. The fifty most recent entries, merged across both, platform-wide and not
              brand-scoped.
              <br />
              Per-brand availability is here too, on its own axis: a brand row says{' '}
              <strong>on</strong>, <strong>off</strong> or <strong>not set</strong> rather than
              live or disabled, because whether a brand carries a title is a different question
              from whether the title works.
            </div>
          </div>

          <Panel>
            {referenceLoading && changes.length === 0 ? (
              <div className="skeleton" style={{ height: '200px' }}>
                &nbsp;
              </div>
            ) : (
              <DataTable
                caption="Game status changes with the reason recorded and the admin who made them"
                columns={changeColumns}
                rows={changes}
                rowKey={(c) => c.id}
                empty={
                  <EmptyState icon="notebook" title="No status changes recorded">
                    No game has changed status yet. This is a real count of nothing, not a
                    placeholder waiting to be filled in.
                  </EmptyState>
                }
              />
            )}
          </Panel>
        </>
      )}

      {/* ── Confirm a status change, and collect the reason it needs ──────
          One dialog for every direction and for both entry points: the bulk bar
          and the drawer produce the same PendingChange, so there is exactly one
          place where the rules are stated and one place they can be got wrong. */}
      <Dialog
        open={pending !== null}
        onClose={() => setPending(null)}
        // A decision that takes a game dark in the catalogue is answered
        // explicitly, and a click outside must not abandon a half-typed reason.
        dismissible={!submitting}
        title={
          pending
            ? CHANGE_TITLE[pending.status](
                pending.ids.length === 1
                  ? (pending.names[0] ?? 'this game')
                  : `${pending.ids.length} games`,
              )
            : 'Change game status'
        }
        actions={
          <>
            <Button onClick={() => setPending(null)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={note.trim().length < 10 || submitting}
              onClick={() => void submitChange()}
            >
              {submitting ? 'Recording…' : 'Record the change'}
            </Button>
          </>
        }
      >
        {pending && (
          <div className="stack">
            <div className="notice notice-quiet">
              <Icon name="warning-circle" />
              <div>{CHANGE_CONSEQUENCE[pending.status]}</div>
            </div>

            {pending.ids.length > 1 && (
              <div>
                <div className="panel-title">
                  {pending.ids.length} games, all or none of them
                </div>
                <div className="row">
                  {/* Named, not counted. "3 games" is not something an operator
                      can check before committing to it. */}
                  {pending.names.slice(0, 12).map((name, i) => (
                    <Tag key={`${name}-${i}`} tone="quiet">
                      {name}
                    </Tag>
                  ))}
                  {pending.names.length > 12 && (
                    <Tag tone="outline">{`+${pending.names.length - 12} more`}</Tag>
                  )}
                </div>
              </div>
            )}

            <Field
              label="Reason"
              hint="A closed set, because the configuration log is a report as well as a story."
            >
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={reasonCode}
                  onChange={(e) => setReasonCode(e.target.value as GameStatusChangeReason)}
                >
                  {STATUS_REASONS.map((r) => (
                    <option key={r.code} value={r.code}>
                      {r.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label="Note"
              error={noteError}
              hint="At least 10 characters. The code says what kind of thing happened; the note says what actually did."
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onBlur={() => setNoteTouched(true)}
                  placeholder="Provider confirmed a settlement fault on the JILI rail at 02:10."
                />
              )}
            </Field>
          </div>
        )}
      </Dialog>

      {/* `holdsManageKey`, not `canManage`. The dialog's refusal copy names ONE
          of two walls, and `canManage` is both of them folded together — so a
          key-holder on an unverified session was told they needed a
          permission they already had. The screen behind this dialog has named
          the two walls separately since the notices were corrected; the
          dialogs had not caught up. */}
      <ProviderStatusDialog
        pending={pendingProvider}
        holdsKey={holdsManageKey}
        mfa={session?.mfa ?? false}
        onClose={() => setPendingProvider(null)}
        onChanged={(result) => {
          const next = writeCount + 1;
          setLastProviderChange({ result, writes: next });
          /* The table's status column and the games' own counts both come from
             the reference load, so the write has to re-read rather than patch
             the row it happens to know about. */
          setWriteCount(next);
        }}
      />

      {/* ALL FOUR TAKE `holdsManageKey` AND `mfa` SEPARATELY, never `canManage`.
          The refusal copy names ONE of two walls, and `canManage` is both folded
          into a boolean — which is how a key-holder on an unverified session
          came to be told they needed a permission they already had. */}
      <AddProviderDialog
        open={addingProvider}
        holdsKey={holdsManageKey}
        mfa={session?.mfa ?? false}
        onClose={() => setAddingProvider(false)}
        onCreated={(result) => {
          const next = writeCount + 1;
          /* A created provider changes the table, the tab counts and the Add
             game form's provider list, so the write RE-READS rather than
             patching the row it happens to know about. */
          setLastCatalogueWrite({
            sentence: `${result.name} (${result.code}) was added, disabled. Bring it live from this table when it is ready.`,
            writes: next,
          });
          setWriteCount(next);
        }}
      />

      <AddGameDialog
        open={addingGame}
        providers={providers}
        holdsKey={holdsManageKey}
        mfa={session?.mfa ?? false}
        onClose={() => setAddingGame(false)}
        onCreated={(result) => {
          const next = writeCount + 1;
          setLastCatalogueWrite({
            sentence: `${result.name} (${result.code}) was added, disabled and on no brand's shelf. Open it to set its brands, then set it live.`,
            writes: next,
          });
          setWriteCount(next);
        }}
      />

      <GameConfigDialog
        pending={editingGame}
        holdsKey={holdsManageKey}
        mfa={session?.mfa ?? false}
        onClose={() => setEditingGame(null)}
        onChanged={(result) => {
          const next = writeCount + 1;
          setLastCatalogueWrite({ sentence: movesSentence(result), writes: next });
          setWriteCount(next);
          /* The drawer holds the row it was opened with, and after this write
             that row is stale. It closes rather than showing fields that
             disagree with the database — the same rule the shelf write follows. */
          setEditingGame(null);
          setDetail(null);
        }}
        onMaybeChanged={() => {
          /* No result to describe, so no sentence under the table: the bump hides
             the last one and re-reads the rows and the Configuration log. */
          setWriteCount(writeCount + 1);
          setEditingGame(null);
          setDetail(null);
        }}
      />

      <ProviderConfigDialog
        pending={editingProvider}
        holdsKey={holdsManageKey}
        mfa={session?.mfa ?? false}
        onClose={() => setEditingProvider(null)}
        onChanged={(result) => {
          const next = writeCount + 1;
          setLastCatalogueWrite({ sentence: movesSentence(result), writes: next });
          setWriteCount(next);
        }}
      />

      <BulkBrandsDialog
        open={bulkBrands}
        gameIds={selectedRows.map((g) => g.id)}
        gameNames={selectedRows.map((g) => g.name)}
        brands={brands}
        holdsKey={holdsManageKey}
        mfa={session?.mfa ?? false}
        onClose={() => setBulkBrands(false)}
        onChanged={(result) => {
          const next = writeCount + 1;
          setLastCatalogueWrite({
            sentence:
              `${String(result.gamesChanged)} title${result.gamesChanged === 1 ? '' : 's'} moved shelf` +
              (result.gamesUnchanged > 0
                ? `; ${String(result.gamesUnchanged)} already held that setting.`
                : '.') +
              ' The reason is on the Configuration log tab.',
            writes: next,
          });
          setWriteCount(next);
          /* The selection's rows are stale now — their Brands column came from
             the read this write just invalidated. */
          setSelected(new Set());
        }}
      />

      <GameBrandsDialog
        pending={pendingBrands}
        holdsKey={holdsManageKey}
        mfa={session?.mfa ?? false}
        onClose={() => setPendingBrands(null)}
        onChanged={(result) => {
          const next = writeCount + 1;
          setLastShelfChange({ result, writes: next });
          setWriteCount(next);
          /* The drawer holds the row it was opened with. After this write that
             row is stale — its brand list is the old one — so the drawer
             closes rather than showing chips that disagree with the database.
             The sentence below says what happened. */
          setDetail(null);
        }}
      />

      {detail && (
        <GameDetail
          shelfLock={shelfLock}
          onEditShelf={setPendingBrands}
          onEditConfig={() =>
            setEditingGame({
              id: detail.id,
              code: detail.code,
              name: detail.name,
              category: detail.category,
              subCategory: detail.sub_category,
              declaredRtpBp: detail.declared_rtp_bp,
              provider: detail.provider,
              driver: detail.provider_driver,
              iconUrl: detail.iconUrl,
              providerIconUrl: detail.providerIconUrl,
              iconSource: detail.iconSource,
              iconUpdatedAt: detail.iconUpdatedAt,
              uploadsReady: pictureUploadsReady,
            })
          }
          game={detail}
          brands={brands}
          brandId={brandId}
          brandLabel={brandLabel}
          statusLock={statusLock}
          onChangeStatus={(status) => askToChange(status, [detail])}
          onClose={() => setDetail(null)}
        />
      )}
    </>
  );
}

/**
 * The game detail drawer.
 *
 * Split out because it is the only surface carrying the declared-versus-measured
 * RTP pair, and because it is where three of the design's eight configuration
 * rows have no column behind them.
 */
function GameDetail({
  game,
  brands,
  brandId,
  brandLabel,
  statusLock,
  shelfLock,
  onEditShelf,
  onEditConfig,
  onChangeStatus,
  onClose,
}: {
  game: GameRow;
  brands: { id: string; name: string }[];
  brandId: string | null;
  brandLabel: string;
  /**
   * Why the status write is unavailable, or null when it is live — the same
   * shape as `shelfLock` below, which this used to disagree with on the same
   * component. It arrives as ONE prop rather than a boolean and a string,
   * because two props that must agree are two props that can be passed
   * disagreeing, and a caller holding both can only get that wrong.
   */
  statusLock: string | null;
  /** Why the per-brand write is unavailable, or null when it is live. */
  shelfLock: string | null;
  onEditShelf: (pending: PendingBrandChange) => void;
  /**
   * Opens the configuration dialog for THIS title.
   *
   * A callback and not a dialog mounted here, because the write closes the
   * drawer: the row this component holds is the one it was opened with, and
   * after an edit it is stale. The parent owns both, so it can close them in
   * the right order.
   */
  onEditConfig: () => void;
  onChangeStatus: (status: GameStatus) => void;
  onClose: () => void;
}) {
  /* Derived here rather than passed, so the drawer cannot be handed a lock and
     a permission that disagree. */
  const canManage = statusLock === null;

  const declared = game.declared_rtp_bp === null ? null : Number(game.declared_rtp_bp);
  const measured = game.measured_rtp_bp === null ? null : Number(game.measured_rtp_bp);
  /* THE SHELF AS IT WAS WHEN THIS DRAWER OPENED, and what the operator has
     since set it to. The write sends the whole set and the route decides what
     moved; this pair exists so the screen can show what is about to change and
     refuse to send a change that changes nothing.

     JOINED BY `brandId`, AND THAT IS THE WHOLE FIX. This used to rebuild the
     set by splitting a joined string and asking `on.includes(b.name)` — a
     match on the DISPLAY NAME, which is not a key: two brands may share a
     name, and a name may contain the `', '` that used to be the delimiter. A
     brand called `"Acme, Inc"` therefore matched nothing, rendered its chip
     OFF while it was on, and SAVING THE DRAWER WROTE THAT BACK — with an
     audit row carrying the operator's own reason, so the one artefact anybody
     would investigate with said it was deliberate.

     The name is presentation only now. Nothing branches on it.

     The map is built INSIDE the memo on purpose: derived outside it, it is a
     new object every render, so the memo would rebuild each time and reset the
     operator's taps mid-edit. */
  const shelfBefore: BrandPair[] = useMemo(() => {
    const on = new Map(game.brandShelfInScope.map((e) => [e.brandId, e.enabled]));
    return brands.map((b) => ({ brandId: b.id, brand: b.name, enabled: on.get(b.id) ?? false }));
  }, [brands, game.brandShelfInScope]);
  const [shelf, setShelf] = useState<Map<string, boolean>>(
    () => new Map(shelfBefore.map((p) => [p.brandId, p.enabled])),
  );
  useEffect(() => {
    setShelf(new Map(shelfBefore.map((p) => [p.brandId, p.enabled])));
  }, [shelfBefore]);
  const shelfMoved = shelfBefore.filter((p) => shelf.get(p.brandId) !== p.enabled).length;

  /*
   * A game cannot return to live behind a provider that is not live.
   *
   * POST /api/games/status refuses it with a 409 — "a provider that is not live
   * blocks every game on its shelf" — and a provider incident is the commonest
   * reason a game is in maintenance in the first place, so this is the ordinary
   * case rather than an edge one. The button is therefore disabled with the
   * reason stated beside it, the way the maker-checker rule is stated on the
   * withdrawal drawer BEFORE it bites, rather than left to come back as a
   * refusal after the operator has chosen a reason and typed a note.
   */
  const providerIsLive = game.provider_status === 'LIVE';
  const providerBlocksLive = game.status === 'MAINTENANCE' && !providerIsLive;

  return (
    <Drawer
      open
      onClose={onClose}
      title={game.name}
      subtitle={`${game.provider} · ${CATEGORY_LABEL[game.category] ?? game.category} · ${game.code}`}
      actions={
        <>
          {/* One button, and it offers the change this game can actually take.
              A "Set to maintenance" on a game already in maintenance is a
              control that can only produce a refusal. */}
          {game.status === 'MAINTENANCE' ? (
            <Button
              disabled={!canManage || !providerIsLive}
              aria-describedby={providerBlocksLive ? 'enable-gap' : undefined}
              onClick={() => onChangeStatus('LIVE')}
            >
              Return to live
            </Button>
          ) : (
            <Button disabled={!canManage} onClick={() => onChangeStatus('MAINTENANCE')}>
              Set to maintenance
            </Button>
          )}
          {game.status !== 'DISABLED' && (
            <Button disabled={!canManage} onClick={() => onChangeStatus('DISABLED')}>
              Disable
            </Button>
          )}
          {/* WIRED. The drawer is handed the opener rather than owning the
              dialog: the dialog closes the drawer on success, because the row
              the drawer holds is the OLD one and re-rendering it would show
              fields that disagree with the database. */}
          <Button variant="primary" disabled={!canManage} onClick={onEditConfig}>
            Edit configuration
          </Button>
        </>
      }
    >
      <div className="row">
        <Tag tone={statusTone(game.status)}>{STATUS_LABEL[game.status] ?? game.status}</Tag>
        <Tag tone={providerIsLive ? 'quiet' : 'outline'}>
          Provider {(STATUS_LABEL[game.provider_status] ?? game.provider_status).toLowerCase()}
        </Tag>
      </div>

      {providerBlocksLive && (
        <div className="notice" id="enable-gap">
          <Icon name="lock-simple" />
          <div>
            <strong>Setting this live is out of reach while {game.provider} is not live.</strong>{' '}
            The change would be refused after you had already chosen a reason, so it is blocked
            here instead. Bring the provider back to live first, on the{' '}
            <strong>Providers</strong> tab, which asks for a reason in the same way. Bringing it
            back does not switch anything on by itself: this game keeps the status it has now, and
            you come back here to move it. <strong>Disable</strong> still works — only the move to
            live is blocked.
          </div>
        </div>
      )}

      {/* The design puts the KPI band here and nowhere else — the list itself has
          no stat row, because a catalogue is a comparison, not a headline. */}
      <div className="stat-grid">
        <Stat label="Rounds 30d" value={formatCount(BigInt(game.rounds))} delta={brandLabel} />
        <Stat label="GGR 30d" value={formatPesos(BigInt(game.ggr))} delta={brandLabel} />
      </div>

      <div className="panel-title">Configuration</div>
      <dl className="stack" style={{ gap: 'var(--space-2)' }}>
        <div className="def-row">
          <dt>Game code</dt>
          <dd>{game.code}</dd>
        </div>
        <div className="def-row">
          <dt>Provider</dt>
          <dd>
            {game.provider} · {game.provider_code}
          </dd>
        </div>
        <div className="def-row">
          <dt>Category</dt>
          <dd>{CATEGORY_LABEL[game.category] ?? game.category}</dd>
        </div>
        <div className="def-row">
          <dt>Declared RTP</dt>
          <dd>
            {declared === null ? (
              /* "(provider, read-only)" must not survive an absent figure: it
                 would attribute a number to a provider that never gave one. */
              <span className="text-subtle">This provider publishes no figure for this game</span>
            ) : (
              `${formatRate(declared)} (provider, read-only)`
            )}
          </dd>
        </div>
        <div className="def-row">
          <dt>Measured RTP 30d</dt>
          <dd>
            {measured === null ? (
              <span className="text-subtle">No settled rounds in 30 days</span>
            ) : (
              formatRate(measured)
            )}
          </dd>
        </div>
        {measured !== null && declared !== null && (
          <div className="def-row">
            {/* The whole reason both numbers are shown. Deviation over 30 days is
                what Reports escalates on; here it is just visible.

                It needs BOTH figures. With no declared RTP there is nothing to
                deviate FROM, and computing it against a null-as-zero would
                report the whole measured figure as the deviation. */}
            <dt>Deviation</dt>
            <dd>{`${measured >= declared ? '+' : '−'}${(Math.abs(measured - declared) / 100).toFixed(2)} pp`}</dd>
          </div>
        )}
        {/* Rule 7: nothing on the launch path could act on these three, so none
            is stored — the notice below says why. A plausible "₱1 – ₱20,000"
            here is prototype copy, and an operator would read it as
            configuration that bites. */}
        <div className="def-row">
          <dt>Bet range</dt>
          <dd>
            <span className="text-subtle">Not stored</span>
          </dd>
        </div>
        <div className="def-row">
          <dt>Jackpot contribution</dt>
          <dd>
            <span className="text-subtle">Not stored</span>
          </dd>
        </div>
        <div className="def-row">
          <dt>Demo mode</dt>
          <dd>
            <span className="text-subtle">Not stored</span>
          </dd>
        </div>
      </dl>

      <div className="notice notice-quiet">
        <Icon name="warning-circle" />
        {/* In the title's own provider's terms: a JILI title used to be told SA's reason. */}
        <div>{notStoredReason(game.provider_driver)}</div>
      </div>

      <div className="panel-title">Brand availability</div>
      {brandId ? (
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            Hidden while you have one brand selected. The list is narrowed to that brand, so every
            other brand would read as &ldquo;off&rdquo; whether or not it really is. Switch the
            brand at the top of the screen to <strong>All</strong> to see the true picture.
          </div>
        </div>
      ) : shelfLock ? (
        <>
          <div className="row">
            {shelfBefore.map((p) => (
              /* Never colour alone: the chip says "off" in words too. */
              <Tag key={p.brandId} tone={p.enabled ? 'accent' : 'quiet'}>
                {p.brand}
                {p.enabled ? '' : ' · off'}
              </Tag>
            ))}
          </div>
          <div className="notice notice-quiet">
            <Icon name="lock-key" />
            <div>Read-only for you: {shelfLock}.</div>
          </div>
        </>
      ) : (
        <>
          <div className="row">
            {shelfBefore.map((p) => {
              const on = shelf.get(p.brandId) ?? p.enabled;
              const moved = on !== p.enabled;
              return (
                <Button
                  key={p.brandId}
                  small
                  {...(on ? { variant: 'primary' as const } : {})}
                  aria-pressed={on}
                  aria-label={`${p.brand}: ${on ? 'on' : 'off'}${moved ? ', changed' : ''}`}
                  onClick={() =>
                    setShelf((current) => {
                      const next = new Map(current);
                      next.set(p.brandId, !on);
                      return next;
                    })
                  }
                >
                  {p.brand}
                  {on ? '' : ' · off'}
                  {moved ? ' ·' : ''}
                </Button>
              );
            })}
          </div>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span className="text-subtle">
              {shelfMoved === 0
                ? 'Tap a brand to add or remove this title from its catalogue.'
                : `${shelfMoved} change${shelfMoved === 1 ? '' : 's'} to confirm — nothing is written until you give a reason.`}
            </span>
            <Button
              variant="primary"
              small
              disabled={shelfMoved === 0}
              onClick={() =>
                onEditShelf({
                  gameId: game.id,
                  gameName: game.name,
                  before: shelfBefore,
                  after: shelf,
                })
              }
            >
              Change the shelf
            </Button>
          </div>
        </>
      )}

      <div className="notice notice-quiet">
        <Icon name={canManage ? 'notebook' : 'lock-key'} />
        <div>
          {statusLock === null ? (
            <>
              Three things here change this title, and each asks you why first: the status
              buttons, brand availability, and <strong>Edit configuration</strong> — which covers
              the name, the category, the table type and the declared RTP. All three are kept on
              the Configuration log tab.
              {providerOwnsTitle(game.provider_driver) && (
                <>
                  {' '}
                  On this title the name and table type are <strong>read-only</strong>:{' '}
                  {game.provider} supplies both and the {hourlyImportName(game.provider_driver)} would overwrite an edit
                  within the hour. The category and the declared RTP are ours and can be changed.
                </>
              )}{' '}
              Bet range, jackpot contribution and demo mode are a different matter, explained above —
              those are not fields waiting to be added.
            </>
          ) : (
            <>
              All three writes on this drawer are out of reach: {statusLock}. The status buttons,
              brand availability and <strong>Edit configuration</strong> are covered by the same
              permission, so it is one wall rather than three — not the platform being unable to do
              any of them. Bet range, jackpot contribution and demo mode are the only things here
              that nothing can write, for the reason stated above.
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
}
